import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tr, Td, Flex, Switch, Text, Box } from "@chakra-ui/react";
import { useClasswizeToast } from "@/components/ui/Toast";
import { colours } from "@/utils/themeUtils";
import { ScheduleDayOfWeek, ScheduleResponse } from "@/api/schedules";
import { formatDateAsLocaleDateString, format24HrTimeInputForDisplay, getDateFromDDMMYYYY, isDateInThePast } from "@/utils/dateTimeUtils";
import { ClasswizeSelect } from "../ui/Select";
import { TimeInput24Hr, TimeInput } from "../TimeInput";
import { DatePickerInput } from "../DatePickerInput";
import {
	useCreateScheduleMutation,
	useDeleteScheduleMutation,
	useToggleScheduleActiveMutation,
	useUpdateScheduleMutation,
} from "@/query/scheduleQuery";
import { OptionButton } from "./OptionButton";
import { ScheduleTableContext } from ".";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useAccountSettingsUse24Hour } from "@/query/accountSettingsQuery";
import {
	captureScheduleCreationOrUpdateEvent,
	captureScheduleDeletionEvent,
	captureScheduleDuplicateEvent,
	captureScheduleToggleEvent,
} from "@/utils/posthogUtils";

export type ScheduleRule = {
	id: number;
	day: ScheduleDayOfWeek;
	startTime: TimeInput24Hr;
	endTime: TimeInput24Hr;
	endDate: Date;
	active: boolean;
	modified: {
		by: string;
		date: string;
	};
};

interface Props {
	classroomId: string;
	/** The row will start in edit mode if this is true */
	isNewSchedule: boolean;
	/** Undefined for new rules that are not a duplicated rule */
	schedule?: ScheduleRule;
	className?: string;
}

function ErrorMessage(props: { error?: string }) {
	return (
		<Box mt={1} height={"16px"}>
			<Text color="#CB4655" fontSize={"sm"}>
				{props.error}
			</Text>
		</Box>
	);
}

export function ScheduleTableRow(props: Props): JSX.Element {
	const { t } = useTranslation();
	const dayLabels = useMemo(
		() =>
			({
				[ScheduleDayOfWeek.Monday]: t("scheduling.dayMonday"),
				[ScheduleDayOfWeek.Tuesday]: t("scheduling.dayTuesday"),
				[ScheduleDayOfWeek.Wednesday]: t("scheduling.dayWednesday"),
				[ScheduleDayOfWeek.Thursday]: t("scheduling.dayThursday"),
				[ScheduleDayOfWeek.Friday]: t("scheduling.dayFriday"),
				[ScheduleDayOfWeek.Saturday]: t("scheduling.daySaturday"),
				[ScheduleDayOfWeek.Sunday]: t("scheduling.daySunday"),
			}) satisfies Record<ScheduleResponse["day"], string>,
		[t]
	);
	const dayOptions = useMemo(() => Object.values(ScheduleDayOfWeek).map((day) => ({ label: dayLabels[day], value: day })), [dayLabels]);

	const { setShowNewScheduleRow, handleNewScheduleRule, registerRowInEditMode, unregisterRowInEditMode, exceedsStudentLimit } =
		useContext(ScheduleTableContext);
	const useToast = useClasswizeToast();

	function successToast(title: string) {
		useToast.closeAll();
		useToast.successToast({
			title,
			isClosable: true,
		});
	}

	function errorToast(title: string, description: string) {
		useToast.closeAll();
		useToast.errorToast({
			title,
			description,
			isClosable: true,
		});
	}

	/**
	 * A single function for the multiple cases we need to handle an error when creating, updating, or activating a schedule.
	 */
	function handleScheduleChangeError(error: Error, action: "create" | "update" | "activate") {
		const couldNotTitle =
			action === "create"
				? t("scheduling.couldNotCreate")
				: action === "update"
					? t("scheduling.couldNotUpdate")
					: t("scheduling.couldNotActivate");
		if (error.message.includes("schedule overlap detected")) {
			errorToast(couldNotTitle, t("scheduling.overlapDescription"));
			return;
		}
		if (error.message.includes("end date cannot be more than 1 year in the future")) {
			errorToast(couldNotTitle, t("scheduling.endDateFutureDescription"));
			return;
		}
		if (error.message.includes("end date cannot be in the past")) {
			errorToast(couldNotTitle, t("scheduling.endDatePastDescription"));
			return;
		}
		if (error.message.includes("outside of school hours")) {
			errorToast(couldNotTitle, t("scheduling.outsideSchoolHoursDescription"));
			return;
		}
		const genericTitle =
			action === "create"
				? t("scheduling.errorCreatingTitle")
				: action === "update"
					? t("scheduling.errorUpdatingTitle")
					: t("scheduling.errorActivatingTitle");
		errorToast(genericTitle, t("errors.unexpectedError"));
	}

	const userSessionStore = useUserSessionStore();
	const use24Hour = useAccountSettingsUse24Hour();
	const classroomName = props.className || t("scheduling.unknownClass");

	const createScheduleMutation = useCreateScheduleMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			successToast(t("scheduling.toastSaved"));
			// Only close the new schedule row and exit edit mode on successful creation
			setShowNewScheduleRow(false);
			unregisterRowInEditMode(props.schedule?.id ?? 0);

			// Capture PostHog event for successful schedule creation
			if (day && startTime && endTime && endDate) {
				captureScheduleCreationOrUpdateEvent(
					false, // isUpdate
					dayLabels[day],
					startTime,
					endTime,
					endDate,
					scheduleActive,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
		},
		onError: (error) => {
			handleScheduleChangeError(error, "create");
		},
	});
	const updateScheduleMutation = useUpdateScheduleMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			successToast(t("scheduling.toastSaved"));
			setInEditMode(false);
			unregisterRowInEditMode(props.schedule?.id ?? 0);
			// Capture PostHog event for successful schedule update
			if (day && startTime && endTime && endDate) {
				captureScheduleCreationOrUpdateEvent(
					true, // isUpdate
					dayLabels[day],
					startTime,
					endTime,
					endDate,
					scheduleActive,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
		},
		onError: (error) => {
			handleScheduleChangeError(error, "update");
		},
	});
	const deleteScheduleMutation = useDeleteScheduleMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			// Capture PostHog event for successful schedule deletion
			if (props.schedule) {
				captureScheduleDeletionEvent(
					dayLabels[props.schedule.day],
					props.schedule.startTime,
					props.schedule.endTime,
					props.schedule.endDate,
					props.schedule.active,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
			successToast(t("scheduling.toastDeleted"));
		},
		onError: () => {
			errorToast(t("scheduling.errorDeletingTitle"), t("errors.unexpectedError"));
		},
	});
	const toggleScheduleActiveMutation = useToggleScheduleActiveMutation(props.classroomId, userSessionStore.getActiveUser() ?? "", {
		onSuccess: () => {
			successToast(t("scheduling.toastSaved"));
			// Capture PostHog event for successful schedule toggle
			if (props.schedule) {
				captureScheduleToggleEvent(
					scheduleActive,
					dayLabels[props.schedule.day],
					props.schedule.startTime,
					props.schedule.endTime,
					props.schedule.endDate,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string,
					classroomName
				);
			}
		},
		onError: (error) => {
			handleScheduleChangeError(error, "activate");
		},
	});

	// Used to determine whether to update the schedule or create a new one, and whether to show the "last updated" information
	const isNewDuplicatedSchedule = props.isNewSchedule && !!props.schedule;

	const [scheduleActive, setScheduleActive] = useState(props.schedule?.active ?? false);

	const [inEditMode, setInEditMode] = useState(props.isNewSchedule);

	const [day, setDay] = useState<ScheduleDayOfWeek | undefined>(props.schedule?.day);
	const [dayError, setDayError] = useState<string | undefined>();

	const [startTime, setStartTime] = useState<TimeInput24Hr | undefined>(props.schedule?.startTime);
	const [startTimeError, setStartTimeError] = useState<string | undefined>();

	const [endTime, setEndTime] = useState<TimeInput24Hr | undefined>(props.schedule?.endTime);
	const [endTimeError, setEndTimeError] = useState<string | undefined>();

	const [endDate, setEndDate] = useState<Date | null>(props.schedule?.endDate ?? null);
	const [endDateError, setEndDateError] = useState<string | undefined>();

	const [hasValidationErrors, setHasValidationErrors] = useState(false);

	function validateDay(dayToValidate?: ScheduleDayOfWeek): boolean {
		const dayValue = dayToValidate ?? day;
		if (!dayValue) {
			setDayError(t("scheduling.validationDayRequired"));
			return false;
		}
		setDayError(undefined);
		return true;
	}

	function validateStartTime(startTimeToValidate?: TimeInput24Hr): boolean {
		const startTimeValue = startTimeToValidate ?? startTime;
		if (!startTimeValue) {
			setStartTimeError(t("scheduling.validationStartRequired"));
			return false;
		}
		setStartTimeError(undefined);
		return true;
	}

	function validateEndTime(endTimeToValidate?: TimeInput24Hr, startTimeToValidate?: TimeInput24Hr): boolean {
		const endTimeValue = endTimeToValidate ?? endTime;
		const startTimeValue = startTimeToValidate ?? startTime;

		if (!endTimeValue) {
			setEndTimeError(t("scheduling.validationEndRequired"));
			return false;
		}
		if (
			startTimeValue &&
			(endTimeValue.hour < startTimeValue.hour ||
				(endTimeValue.hour === startTimeValue.hour && endTimeValue.minute <= startTimeValue.minute))
		) {
			setEndTimeError(t("scheduling.validationEndAfterStart"));
			return false;
		}
		setEndTimeError(undefined);
		return true;
	}

	function validateEndDate(endDateToValidate?: Date): boolean {
		const endDateValue = endDateToValidate ?? endDate;
		if (!endDateValue) {
			setEndDateError(t("scheduling.validationEndDateRequired"));
			return false;
		}
		// Compare dates only, not datetime to avoid timezone issues
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const compareDate = new Date(endDateValue);
		compareDate.setHours(0, 0, 0, 0);

		if (compareDate < today) {
			setEndDateError(t("scheduling.validationEndDatePast"));
			return false;
		}

		// Check if end date is more than one year in the future
		const oneYearFromToday = new Date(today);
		oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

		if (compareDate > oneYearFromToday) {
			setEndDateError(t("scheduling.validationEndDateYear"));
			return false;
		}

		setEndDateError(undefined);
		return true;
	}

	function validateSchedule(): boolean {
		if (!validateDay()) return false;
		if (!validateStartTime()) return false;
		if (!validateEndTime()) return false;
		if (!validateEndDate()) return false;
		return true;
	}

	// Sync state when schedule changes
	useEffect(() => {
		if (props.schedule) {
			setDay(props.schedule.day);
			setStartTime(props.schedule.startTime);
			setEndTime(props.schedule.endTime);
			setEndDate(props.schedule.endDate);
			setScheduleActive(props.schedule.active);
		}
	}, [props.schedule]);

	useEffect(() => {
		setHasValidationErrors(!!dayError || !!startTimeError || !!endTimeError || !!endDateError);
	}, [dayError, startTimeError, endTimeError, endDateError]);

	function handleDeleteSchedule(scheduleId: number) {
		deleteScheduleMutation.mutate(scheduleId);
	}

	function handleDuplicateSchedule(schedule: ScheduleRule) {
		// Capture PostHog event for schedule duplication
		captureScheduleDuplicateEvent(userSessionStore.getActiveUser() as string, userSessionStore.getActiveDevice() as string, classroomName);

		handleNewScheduleRule(schedule);
	}

	function handleToggleEnable(e: React.ChangeEvent<HTMLInputElement>) {
		setScheduleActive(e.target.checked);

		if (inEditMode) return;

		toggleScheduleActiveMutation.mutate({
			scheduleId: props.schedule?.id as number,
			active: e.target.checked,
		});
	}

	function saveChanges() {
		if (!validateSchedule()) {
			return;
		}

		// Validation passed, so these values should be defined
		if (!day || !startTime || !endTime || !endDate) {
			console.error("Validation passed but required fields are missing");
			return;
		}

		if (!isNewDuplicatedSchedule && props.schedule?.id) {
			updateScheduleMutation.mutate({
				id: props.schedule.id,
				day,
				startTime,
				endTime,
				endDate,
				active: scheduleActive,
			});
		} else {
			createScheduleMutation.mutate({
				day,
				startTime,
				endTime,
				endDate,
				active: scheduleActive,
			});
		}

		// Note: For new schedules, the row closing is handled in the mutation success callback to ensure the row stays open if creation fails
	}

	function cancelChanges() {
		if (props.isNewSchedule) {
			setDay(undefined);
			setStartTime(undefined);
			setEndTime(undefined);
			setEndDate(null);
			setScheduleActive(false);
			setDayError(undefined);
			setStartTimeError(undefined);
			setEndTimeError(undefined);
			setEndDateError(undefined);
			unregisterRowInEditMode(props.schedule?.id ?? 0);

			setShowNewScheduleRow(false);
			return;
		}

		// Revert to original values from props
		setDay(props.schedule?.day);
		setStartTime(props.schedule?.startTime);
		setEndTime(props.schedule?.endTime);
		setEndDate(props.schedule?.endDate ?? null);
		setScheduleActive(props.schedule?.active ?? false);

		// Clear any validation errors
		setDayError(undefined);
		setStartTimeError(undefined);
		setEndTimeError(undefined);
		setEndDateError(undefined);

		setInEditMode(false);
		unregisterRowInEditMode(props.schedule?.id ?? 0);
	}

	return (
		<>
			<Tr
				key={props.schedule?.id}
				backgroundColor={hasValidationErrors ? "#ffeeee !important" : inEditMode ? "#e8f8ef !important" : "transparent"}
			>
				<Td px={4}>
					{inEditMode ? (
						<>
							<ClasswizeSelect
								width="150px"
								truncateLabel
								value={day}
								onChange={(value) => {
									setDay(value as ScheduleDayOfWeek);
									validateDay(value as ScheduleDayOfWeek);
								}}
								placeholder={t("scheduling.placeholderSelectDay")}
								options={dayOptions}
							/>
							{hasValidationErrors && <ErrorMessage error={dayError} />}
						</>
					) : (
						dayLabels[day ?? ScheduleDayOfWeek.Monday]
					)}
				</Td>
				<Td px={4}>
					{inEditMode ? (
						<>
							<TimeInput
								value={startTime}
								use24Hour={use24Hour}
								handleChange={(value) => {
									setStartTime(value);
									validateStartTime(value);
									// Re-validate end time since it depends on start time
									if (endTime) {
										validateEndTime(endTime, value);
									}
								}}
							/>
							{hasValidationErrors && <ErrorMessage error={startTimeError} />}
						</>
					) : (
						format24HrTimeInputForDisplay(props.schedule?.startTime ?? { hour: 0, minute: 0 }, use24Hour)
					)}
				</Td>
				<Td px={4}>
					{inEditMode ? (
						<>
							<TimeInput
								value={endTime}
								use24Hour={use24Hour}
								handleChange={(value) => {
									setEndTime(value);
									validateEndTime(value, startTime);
								}}
							/>
							{hasValidationErrors && <ErrorMessage error={endTimeError} />}
						</>
					) : (
						format24HrTimeInputForDisplay(props.schedule?.endTime ?? { hour: 0, minute: 0 }, use24Hour)
					)}
				</Td>
				<Td px={4} color={isDateInThePast(endDate as Date) ? colours.danger.icon : "inherit"}>
					{inEditMode ? (
						<>
							<DatePickerInput
								value={endDate}
								width="175px"
								onDateChange={(date) => {
									if (date) {
										setEndDate(date);
										validateEndDate(date);
									}
								}}
								isInvalid={hasValidationErrors || (!!endDate && isDateInThePast(endDate))}
							/>
							{hasValidationErrors && <ErrorMessage error={endDateError} />}
						</>
					) : (
						formatDateAsLocaleDateString(endDate as Date)
					)}
				</Td>
				<Td px={2} textAlign="center">
					<Switch
						isChecked={scheduleActive}
						onChange={handleToggleEnable}
						isDisabled={toggleScheduleActiveMutation.isLoading || exceedsStudentLimit}
					/>
					{hasValidationErrors && <ErrorMessage />}
				</Td>
				<Td px={4}>
					{inEditMode ? (
						<Flex key="edit-mode-buttons" gap={2} justifyContent="center">
							<OptionButton
								iconName="fa-check"
								label={t("scheduling.buttonSave")}
								onClick={saveChanges}
								isDisabled={hasValidationErrors || createScheduleMutation.isLoading || updateScheduleMutation.isLoading}
							/>
							<OptionButton
								iconName="fa-xmark"
								label={t("scheduling.buttonCancel")}
								onClick={cancelChanges}
								isDisabled={createScheduleMutation.isLoading || updateScheduleMutation.isLoading}
							/>
						</Flex>
					) : (
						<Flex key="view-mode-buttons" gap={2} justifyContent="center">
							<OptionButton
								iconName="fa-pencil"
								label={t("scheduling.buttonEdit")}
								onClick={() => {
									setInEditMode(true);
									registerRowInEditMode(props.schedule?.id ?? 0);
								}}
								isDisabled={
									hasValidationErrors || deleteScheduleMutation.isLoading || toggleScheduleActiveMutation.isLoading || exceedsStudentLimit
								}
							/>
							<OptionButton
								iconName="fa-copy"
								label={t("scheduling.buttonDuplicate")}
								onClick={() => handleDuplicateSchedule(props.schedule as ScheduleRule)}
								isDisabled={
									hasValidationErrors || deleteScheduleMutation.isLoading || toggleScheduleActiveMutation.isLoading || exceedsStudentLimit
								}
							/>
							<OptionButton
								iconName="fa-trash"
								label={t("scheduling.buttonDelete")}
								onClick={() => handleDeleteSchedule(props.schedule?.id ?? 0)}
								isDisabled={hasValidationErrors || deleteScheduleMutation.isLoading || toggleScheduleActiveMutation.isLoading}
							/>
						</Flex>
					)}
					{hasValidationErrors && <ErrorMessage />}
				</Td>
			</Tr>
			{!isNewDuplicatedSchedule && props.schedule?.modified && (
				<>
					{/* The additional hidden row is required to ensure the striping of the table is visually maintained */}
					<Tr hidden></Tr>
					<Tr backgroundColor={hasValidationErrors ? "#FEE4E299 !important" : inEditMode ? "#e8f8ef !important" : "transparent"}>
						<Td colSpan={6} pt={0} pb={2} px={4}>
							<Text fontSize="sm" color={colours.gray[700]} mt={-2} mb={2}>
								{t("scheduling.lastUpdated", {
									by: props.schedule?.modified.by,
									date: formatDateAsLocaleDateString(getDateFromDDMMYYYY(props.schedule?.modified.date as string)),
								})}
							</Text>
						</Td>
					</Tr>
				</>
			)}
		</>
	);
}
